import { useEffect, useState } from "react";

const RO = "#FF5C7A";
const PL = "#4B1F4F";
const GR = "#6E4B71";
const BG = "#FAF7F5";
const WH = "#ffffff";

const DISMISS_KEY = "menoiq_install_dismissed_at";
const NEVER_KEY = "menoiq_install_never";
const DISMISS_WINDOW_DAYS = 14;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function recentlyDismissed(): boolean {
  const ts = safeGet(DISMISS_KEY);
  if (!ts) return false;
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return false;
  const ageDays = (Date.now() - t) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_WINDOW_DAYS;
}

export function InstallPrompt({ milestoneDay }: { milestoneDay: number }) {
  const [eligible, setEligible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [bipEvent, setBipEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Suppress in iframes / Lovable preview hosts
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    const host = window.location.hostname;
    const isPreviewHost =
      host.includes("id-preview--") || host.includes("lovableproject.com");
    if (inIframe || isPreviewHost) return;

    // Already installed?
    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (navigator as any).standalone === true;
    if (standalone) return;

    if (safeGet(NEVER_KEY) === "1") return;
    if (recentlyDismissed()) return;
    if (!milestoneDay || milestoneDay < 1) return;

    const ios =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    if (ios) {
      // No programmatic install on iOS — show instructions card.
      setEligible(true);
      return;
    }

    const onBip = (e: Event) => {
      e.preventDefault();
      setBipEvent(e as BeforeInstallPromptEvent);
      setEligible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    const onInstalled = () => {
      setEligible(false);
      setBipEvent(null);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [milestoneDay]);

  if (!eligible) return null;

  const dismissShort = () => {
    safeSet(DISMISS_KEY, new Date().toISOString());
    setEligible(false);
  };
  const dismissForever = () => {
    safeSet(NEVER_KEY, "1");
    setEligible(false);
  };
  const handleInstall = async () => {
    if (!bipEvent) return;
    try {
      await bipEvent.prompt();
      const choice = await bipEvent.userChoice;
      if (choice.outcome === "accepted") {
        setEligible(false);
      } else {
        dismissShort();
      }
    } catch {
      dismissShort();
    } finally {
      setBipEvent(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Install KnowMeno"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(20, 8, 18, 0.45)",
        fontFamily: "system-ui, sans-serif",
      }}
      onClick={dismissShort}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          margin: "0 12px 12px",
          background: BG,
          borderRadius: 24,
          padding: "22px 22px 18px",
          boxShadow: "0 30px 80px -30px rgba(74, 25, 66, 0.45)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div
            aria-hidden
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: "#F5E9F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="22" height="26" viewBox="0 0 22 26" fill="none" aria-hidden>
              <path d="M11 2 L11 17" stroke="#4B1F4F" strokeWidth="2" strokeLinecap="round" />
              <path d="M6 7 L11 2 L16 7" stroke="#4B1F4F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M3 11 L3 23 L19 23 L19 11" stroke="#4B1F4F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontSize: 22,
                lineHeight: 1.15,
                color: "#4B1F4F",
                fontWeight: 500,
                letterSpacing: "-0.01em",
              }}
            >
              Add KnowMeno to your home screen
            </div>
          </div>
        </div>
        <p
          style={{
            color: GR,
            fontSize: 14,
            lineHeight: 1.5,
            margin: "0 0 16px",
          }}
        >
          One tap to open. No browser bars. Same app — just faster to reach.
        </p>

        {isIOS ? (
          <div
            style={{
              background: WH,
              border: "1px solid #FAF7F5",
              borderRadius: 14,
              padding: "12px 14px",
              fontSize: 13.5,
              color: "#4B1F4F",
              lineHeight: 1.55,
              marginBottom: 14,
            }}
          >
            <div style={{ marginBottom: 6, fontWeight: 500 }}>On iPhone or iPad:</div>
            <div style={{ marginBottom: 4 }}>
              1. Tap the <strong>Share</strong> icon{" "}
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 16,
                  height: 16,
                  verticalAlign: "-3px",
                  border: `1.5px solid ${PL}`,
                  borderRadius: 3,
                  position: "relative",
                  marginRight: 2,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: -4,
                    width: 2,
                    height: 9,
                    background: PL,
                    transform: "translateX(-50%)",
                  }}
                />
              </span>{" "}
              (square with an arrow pointing up) in your browser toolbar. If you don't see it, tap the <strong>⋯</strong> (three dots) menu first, then tap <strong>Share</strong>.
            </div>
            <div style={{ marginBottom: 4 }}>
              2. Scroll down in the share menu, or tap <strong>View More</strong> if you don't see "Add to Home Screen" yet.
            </div>
            <div style={{ marginBottom: 4 }}>
              3. Tap <strong>Add to Home Screen</strong>.
            </div>
            <div>
              4. Tap <strong>Add</strong> in the top-right corner to confirm.
            </div>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            style={{
              width: "100%",
              background: PL,
              color: WH,
              border: "none",
              borderRadius: 14,
              padding: 14,
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
              marginBottom: 10,
            }}
          >
            Install
          </button>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            onClick={dismissShort}
            style={{
              background: "transparent",
              color: PL,
              border: `1px solid ${PL}`,
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 13.5,
              cursor: "pointer",
              flex: isIOS ? 1 : "0 0 auto",
            }}
          >
            Not now
          </button>
          <button
            onClick={dismissForever}
            style={{
              background: "transparent",
              color: GR,
              border: "none",
              padding: "8px 4px",
              fontSize: 12.5,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Don't ask again
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
